import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CircleCheck, Globe, MousePointerClick, UserPlus } from 'lucide-react';
import { DEMO_CTA_HREF } from '@/config';
import { session, type Artifact, type Destination, type EventIcon, type Session } from '@/data/session';
import { ColorMark } from './IntegrationLogos';

// The chips Foxy tosses: text, never logos. Jira is direction only.
const TOKEN: Record<NonNullable<Destination>, string> = { salesforce: 'SF', hubspot: 'HS', slack: 'Slack', jira: 'Jira · soon', insights: 'Insights' };

const eventIcons: Record<EventIcon, typeof Globe> = { globe: Globe, cta: MousePointerClick, 'user-plus': UserPlus, captured: CircleCheck };
import { Volume2, VolumeX } from 'lucide-react';
import { setSoundEnabled, soundEnabled } from './guide/sounds';
import { useStage, type LedgerStepInfo, type Dir } from './guide/stage';
import './session-ledger.css';

function localTime(iso: string) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function ArtifactCard({ artifact, on, fresh, cardRef }: { artifact: Artifact; on: boolean; fresh: boolean; cardRef: (el: HTMLDivElement | null) => void }) {
  const cls = `fx-art${on ? ' on' : ''}${fresh ? ' new' : ''}`;
  if (artifact.kind === 'crm') {
    return (
      <div className={cls} ref={cardRef}>
        <div className="fx-art-crm">
          <ColorMark name={{ salesforce: 'Salesforce', hubspot: 'HubSpot', zoho: 'Zoho' }[artifact.system]} />
          <span>{artifact.body}</span>
        </div>
      </div>
    );
  }
  if (artifact.kind === 'brief') {
    return (
      <div className={cls} ref={cardRef}>
        <b>{artifact.title}</b>
        {Object.entries(artifact.fields)
          .map(([k, v]) => `${k}: ${v}`)
          .join(' · ')}
      </div>
    );
  }
  return (
    <div className={cls} ref={cardRef}>
      <b>{artifact.title}</b>
      {artifact.body}
    </div>
  );
}

export function SessionLedger({ data = session }: { data?: Session }) {
  const { steps, artifacts } = data;
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [reduced, setReduced] = useState(false);
  const [active, setActive] = useState(0);
  const [panePulse, setPanePulse] = useState(false);
  const [dotPulse, setDotPulse] = useState(false);
  const [sound, setSound] = useState(false);
  const viewRef = useRef<HTMLDivElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const slotClippy = useRef<HTMLSpanElement>(null);
  const slotFoxy = useRef<HTMLSpanElement>(null);
  const mailFromRef = useRef<HTMLSpanElement>(null);
  const artRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const stage = useStage();
  // The ledger owns the characters while ≥ 60% of the pane is in the viewport. Mr. Clippy arrives
  // (act 1) when that holds on step 1, after a 250ms settle so a continuing scroll does not catch
  // him mid-air; the arrival plays once per load.
  const [paneSeen, setPaneSeen] = useState(false);
  // the arrival fires earlier than ownership: the pane ≥ 35% visible, or step 1 active with the pane in view
  const [paneRatio, setPaneRatio] = useState(0);
  const boundary = useRef<{ dir: Dir }>({ dir: 'down' });
  const lastY = useRef(0);
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    // ownership with hysteresis: the ledger takes the characters at ≥ 35% of the pane visible and releases
    // them below 15%, so a reader hovering at the boundary never flickers
    // the scroll direction at a threshold crossing (from the scroll delta since the previous one): leaving downward
    // before the story reached step 14 fires B12/B13 as fades; leaving upward changes nothing
    const io = new IntersectionObserver(([entry]) => {
      const r = entry.isIntersecting ? entry.intersectionRatio : 0;
      const y = window.scrollY;
      boundary.current = { dir: y >= lastY.current ? 'down' : 'up' };
      lastY.current = y;
      setPaneRatio(r);
      setPaneSeen((was) => (was ? r >= 0.15 : r >= 0.35));
    }, { threshold: [0, 0.15, 0.35, 0.6, 1] });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => setSound(soundEnabled()), []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    if (mq.matches) return;
    // The active step is the one whose box centre is nearest the viewport's middle. Computed
    // on scroll rather than observed, so the active block's own padding change cannot re-trigger it.
    let frame = 0;
    const pick = () => {
      frame = 0;
      const mid = window.innerHeight / 2;
      let best = 0;
      let dist = Infinity;
      stepRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const d = Math.abs((r.top + r.bottom) / 2 - mid);
        if (d < dist) {
          dist = d;
          best = i;
        }
      });
      setActive(best);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(pick);
    };
    pick();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [steps.length]);

  const current = reduced ? steps.length - 1 : active;
  const step = steps[current];
  const frameIndex = useMemo(() => {
    for (let i = current; i >= 0; i -= 1) {
      if (steps[i].pane === 'greeting') return -1;
      if (steps[i].frame) return i;
    }
    return -1;
  }, [steps, current]);
  const frameStep = frameIndex >= 0 ? steps[frameIndex] : undefined;
  const frameSrc = frameStep?.frame;
  const seenFrame = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (reduced) return;
    if (seenFrame.current === undefined) {
      seenFrame.current = frameSrc ?? '';
      return;
    }
    if (seenFrame.current === (frameSrc ?? '')) return;
    seenFrame.current = frameSrc ?? '';
    setPanePulse(true);
    const t = window.setTimeout(() => setPanePulse(false), 500);
    return () => window.clearTimeout(t);
  }, [frameSrc, reduced]);
  const seenStep = useRef(-1);
  useEffect(() => {
    if (reduced) return;
    if (seenStep.current === -1) {
      seenStep.current = current;
      return;
    }
    if (seenStep.current === current) return;
    seenStep.current = current;
    setDotPulse(true);
    const t = window.setTimeout(() => setDotPulse(false), 500);
    return () => window.clearTimeout(t);
  }, [current, reduced]);
  // The most recently produced artifacts: from the latest step up to the active one that produced any.
  const newest = useMemo(() => {
    for (let i = current; i >= 0; i -= 1) if (steps[i].artifactIds?.length) return new Set(steps[i].artifactIds);
    return new Set<string>();
  }, [steps, current]);
  const caption = step.frameCaption ?? frameStep?.frameCaption ?? 'Greeting';
  const frames = useMemo(
    () => Array.from(new Set(steps.map((s) => s.frame).filter(Boolean) as string[])),
    [steps],
  );
  const order = useMemo(() => {
    const ids: string[] = [];
    steps.forEach((s) => s.artifactIds?.forEach((id) => !ids.includes(id) && ids.push(id)));
    artifacts.forEach((a) => !ids.includes(a.id) && ids.push(a.id));
    return ids.map((id) => artifacts.find((a) => a.id === id)).filter(Boolean) as Artifact[];
  }, [steps, artifacts]);
  const visible = useMemo(() => {
    const ids = new Set<string>();
    steps.slice(0, current + 1).forEach((s) => s.artifactIds?.forEach((id) => ids.add(id)));
    return ids;
  }, [steps, current]);
  const time = localTime(data.recordedAt);
  const stepInfo = useCallback((): LedgerStepInfo | null => {
    if (!hostRef.current || !viewRef.current || !containerRef.current) return null;
    return {
      index: current,
      total: steps.length,
      step,
      paneHost: hostRef.current,
      viewEl: viewRef.current,
      slots: !frameStep && slotClippy.current && slotFoxy.current ? { clippy: slotClippy.current, foxy: slotFoxy.current } : null,
      mailFromEl: mailFromRef.current,
      containerEl: containerRef.current,
      sectionEl: sectionRef.current ?? containerRef.current,
      landing: steps[0].clippy ? { x: steps[0].clippy.x, y: steps[0].clippy.y } : { x: 31, y: 85 },
    };
  }, [current, frameStep, step, steps.length]);
  useEffect(() => {
    if (!stage) return;
    const info = stepInfo();
    if (!info) return;
    // the current step first (so the stage knows where he stands), then ownership: B1 the first time the pane is
    // ≥ 35% visible; leaving downward (≤ 15%) before step 14 fires B12/B13 as fades
    stage.ledgerStep(info);
    stage.ledgerOwns(paneSeen, info, boundary.current.dir);
  }, [current, paneSeen, stage, stepInfo]);
  // tosses: Foxy flicks a destination chip toward each newly revealed artifact (held ones get none)
  const tossed = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!stage || !paneSeen) return;
    for (const a of artifacts) {
      if (!visible.has(a.id) || tossed.current.has(a.id)) continue;
      tossed.current.add(a.id);
      if (!a.destination) continue;
      const el = artRefs.current[a.id];
      if (!el || !newest.has(a.id)) continue;
      stage.toss(TOKEN[a.destination], a.destination, el);
    }
  }, [artifacts, newest, paneSeen, stage, visible]);
  const ring = step.clippy?.target && step.clippy.action === 'point' ? step.clippy.target : null;

  return (
    <section className="fx-ledger" id="session" ref={sectionRef} data-placeholder={data.placeholder}>
      <div className="container" ref={containerRef}>
        <div className="fx-ledger-head fx-rv">
          <p className="fx-eyebrow">One session, end to end</p>
          <h2>Watch a question become a demo, a brief and a next step.</h2>
        </div>
        {/* the owner (2026-10-16): no placeholder label on the page — it conceptually shows the product; `placeholder: true`
            stays in the data and LANDING_REQUIRE_REAL_SESSION remains the launch check */}
        {!data.placeholder && <p className="fx-ledger-label">A real session · lightly edited · {data.product.name}</p>}
        <div className="fx-ledger-meta">
          <span>
            <i aria-hidden="true" />
            Foxet demoing Foxet
          </span>
          <span>agent {data.agent}</span>
          <span>{data.languages.join(' → ')}</span>
          {time && <span>{time}</span>}
          <button
            type="button"
            className="fx-sound"
            aria-pressed={sound}
            aria-label={sound ? 'Guide sound on. Turn off' : 'Guide sound off. Turn on'}
            onClick={() => {
              setSoundEnabled(!sound);
              setSound(!sound);
            }}
          >
            {sound ? <Volume2 size={16} aria-hidden="true" /> : <VolumeX size={16} aria-hidden="true" />}
            {sound ? 'sound on' : 'sound off'}
          </button>
        </div>
        <div className="fx-lg">
          <div className="fx-steps">
            {steps.map((s, i) => {
              const state = i === current ? `on${dotPulse ? ' pulse' : ''}` : i < current ? `done${reduced ? ' rm' : ''}` : reduced ? 'rm' : '';
              return (
                <div
                  key={`${s.t}-${i}`}
                  ref={(el) => {
                    stepRefs.current[i] = el;
                  }}
                  className={`fx-step ${state}${s.kind === 'event' ? ' event' : ''}`}
                >
                  <div className="fx-step-t">
                    {s.t}
                    {s.kind !== 'event' && ` · ${s.kind === 'buyer' ? 'Buyer' : 'Foxet'}`}
                  </div>
                  {s.kind === 'event' ? (
                    <div className="fx-step-event">
                      {(() => {
                        const Icon = eventIcons[s.icon ?? 'globe'];
                        return <Icon size={14} aria-hidden="true" />;
                      })()}
                      <span>{s.text}</span>
                    </div>
                  ) : s.kind === 'buyer' ? (
                    <div className="fx-step-buyer">“{s.text}”</div>
                  ) : (
                    <div className="fx-step-fox">
                      <img src="/fox-icon.svg" alt="" width="16" height="16" />
                      <span>{s.text}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="fx-sticky fx-browser-col">
            <div className={`fx-browser${panePulse ? ' pulse' : ''}`} ref={paneRef}>
              <div className="fx-browser-bar">
                <i /> <i /> <i />
                <span>{data.product.url} · live</span>
                <em>
                  step {current + 1} / {steps.length}
                </em>
              </div>
              <div className="fx-browser-progress" aria-hidden="true">
                <i style={{ width: `${((current + 1) / steps.length) * 100}%` }} />
              </div>
              <div className="fx-browser-view" ref={viewRef} aria-label="Mr. Clippy, a buyer, and Foxy, the Foxet expert, walk through one session">
                <div className={`fx-browser-frames${frameStep ? '' : ' greeting'}`}>
                  {!frameStep && (
                    <div className="fx-greeting">
                      <div className="fx-greeting-pair">
                        <span className="fx-greeting-slot clippy" ref={slotClippy} aria-hidden="true" />
                        <span className="fx-listening" aria-hidden="true">
                          <i />
                          <i />
                          <i />
                        </span>
                        <span className="fx-greeting-slot foxy" ref={slotFoxy} aria-hidden="true" />
                      </div>
                      <span>the expert greets the buyer · listening</span>
                    </div>
                  )}
                  {frames.map((src) => (
                    <img
                      key={src}
                      src={src}
                      alt=""
                      className={frameStep?.frame === src ? 'on' : undefined}
                    />
                  ))}
                  <span className="fx-mail-from" ref={mailFromRef} aria-hidden="true" />
                  {ring && <i className="fx-char-ring" key={`ring-${current}`} style={{ left: `${ring.x}%`, top: `${ring.y}%` }} aria-hidden="true" />}
                </div>
                <div className="fx-guide-layer" ref={hostRef} />
              </div>
              <div className="fx-browser-cap">
                <b>{caption}</b>
                <i>driven by the expert</i>
              </div>
              <div className="fx-stamps">
                {step.flow ? (
                  <span className={`fx-stamp ${step.flow.status === 'narrated' ? 'na' : ''}`}>
                    flow · {step.flow.name} ·{' '}
                    {step.flow.status === 'verified'
                      ? `verified ${step.flow.verifiedAt ?? ''}`.trim()
                      : 'not verified · narrated only'}
                  </span>
                ) : (
                  <span className="fx-stamp idle">no flow running</span>
                )}
              </div>
            </div>
          </div>
          <div className="fx-sticky fx-arts">
            <p className="fx-arts-t">What the session produced</p>
            {order.map((a) => (
              <ArtifactCard key={a.id} artifact={a} on={reduced || visible.has(a.id)} fresh={!reduced && visible.has(a.id) && newest.has(a.id)} cardRef={(el) => { artRefs.current[a.id] = el; }} />
            ))}
          </div>
        </div>
        <div className="fx-ledger-foot">
          <a href={DEMO_CTA_HREF}>Try it live →</a>
          {data.transcriptHref && <a href={data.transcriptHref}>Read the full transcript →</a>}
        </div>
      </div>
    </section>
  );
}
